/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Visitor, {VisitorDirection, VisitorOrdering} from "App/utils/globe/chunk_lod/visitor";
import IAddress from "App/utils/globe/chunk_lod/adress";
import {ChunkLODNodeState} from "App/utils/globe/chunk_lod/tree/chunk_lod_node";
import {vec3} from "gl-matrix";

/**
 * Abstract class for implementing the tree structure
 *
 * @param aParent
 * @constructor
 */
export default
abstract class TreeNode {
    private _parent: TreeNode;
    protected _UID: string;
    protected _children: TreeNode[];
    protected _data: any;
    protected _requireChildren: boolean;
    protected _isUsed = false;

    constructor(aParent: TreeNode, nc: number) {
        this._parent = aParent;
        this._UID = null;
        this._children = [];
        this._requireChildren = false;

        for (let i = 0; i < nc; ++i) {
            this._children.push(null);
        }
    }

    public abstract createChild(aIndex: number): TreeNode;
    public abstract getAttributes(aAddress: IAddress, aAttributes: any);
    public abstract setState(ChunkLODNodeState);
    public abstract getState(): ChunkLODNodeState;
    public abstract testAllChildrenHasState(ChunkLODNodeState): boolean
    public abstract getCenter(): vec3;
    public abstract setData(aData: any);
    public abstract hasValidDataPath(): boolean;
    public abstract getMetadata(): any;
    public abstract getIndices(): ArrayBuffer;
    public abstract getTextureData(): ArrayBuffer;
    public abstract getRequireDataForChildren(aView: any): boolean;
    public static getFlattenedTreeSize(levels: number, nc: number) {
        return Math.floor((nc * Math.pow(nc, levels) - 1) / (nc - 1));
    }

    public static getIndexToFlattenedTree(address: IAddress): number {
        if (address.level === 0) {
            return 0;
        }

        // we have not the index of level begin nad we need the index based on the address
        return TreeNode.getFlattenedTreeSize(address.level - 1, address.patchInfo.nc) + address.layer * Math.pow(4, address.level) + address.row * Math.pow(2, address.level) + address.col;
    }

    public getUID(): string {
        return this._UID;
    }

    public setIsUsed(aValue: boolean) {
        this._isUsed = aValue;
    }

    public getIsUsed(): boolean {
        return this._isUsed;
    }

    public setRequireChildren(aValue: boolean) {
        this._requireChildren = aValue;
    }

    public getRequireChildren(): boolean {
        return this._requireChildren;
    }

    public hasParent(): boolean {
        return this._parent !== null;
    }

    public getAddress(): IAddress {
        return null;
    }

    public getParent(): TreeNode {
        return this._parent;
    }

    public getChildren(): TreeNode[] {
        return this._children;
    }

    public getNofVertices(aAddress: IAddress) {
        let parent = this.getParent();
        let nofPointsInParent = 0;
        if (parent) {
            nofPointsInParent = parent.getNofVertices(aAddress);
        }

        return nofPointsInParent + this._data.getIncrementPointsCount(aAddress);
    }

    public createChildren() {
        for (let i = 0; i < 4; i++) {
            if (!this._children[i]) {
                this.createChild(i);
            }
        }
    }

    public removeChild(aChild: TreeNode) {
        for (let i = 0; i < this._children.length; i++) {
            if (this._children[i] === aChild) {
                this._children[i] = null;
                break;
            }
        }
    }

    public getEmptyChildrenIndices(): number[] {
        let indices = [];
        for (let i = 0; i < this._children.length; i++) {
            if (!this._children[i]) {
                indices.push(i);
            }
        }

        return indices;
    }

    public getExistingChildrenIndices(): number[] {
        let indices = [];
        for (let i = 0; i < this._children.length; i++) {
            if (this._children[i]) {
                indices.push(i);
            }
        }

        return indices;
    }

    public getChild(aIndex: number): TreeNode {
        return this._children[aIndex];
    }

    public setChild(aIndex: number, aNode: TreeNode) {
        this._children[aIndex] = aNode;
    }

    public hasChildren(): boolean {
        for (let i = 0; i < this._children.length; i++) {
            if (!this._children[i]) {
                return false;
            }
        }

        return true;
    }

    public accept(aVisitor: Visitor) {
        if (aVisitor.getOrdering() === VisitorOrdering.pre) {
            aVisitor.visit(this);
        }

        switch (aVisitor.getDirection()) {
            case VisitorDirection.down:
                for (let i = 0; i < this._children.length; i++) {
                    if (this._children[i]) {
                        this._children[i].accept(aVisitor);
                    }
                }
                break;

            case VisitorDirection.up:
                if (this._parent) {
                    this._parent.accept(aVisitor);
                }
                break;
            default:
                break;
        }

        if (aVisitor.getOrdering() === VisitorOrdering.post) {
            aVisitor.visit(this);
        }
    }
}
